import { useEffect, useState } from 'react';
import Loader from '../../components/loader';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { errorMessage } from '../../utils/utils';
import { ListHeader } from '../../components/ListToolbar';
import { Button } from '../../components/ui';
import { peso, TOPUP_MAXIMUM, TOPUP_MINIMUM, useCredits } from '../../components/Credits';
import { PaymentNoticeBanner } from '../../components/PaymentNotice';
import qrph from '../../assets/qrph-logo.svg';
import gcash from '../../assets/gcash-logo.svg';
import maya from '../../assets/paymaya-logo.svg';
import shopee from '../../assets/shopeepay-logo-2.svg';
import grab from '../../assets/grabpay-logo.svg';

const presets = [100, 200, 500, 1000];

/**
 * Top-ups carry no service fee: the customer pays exactly the credits they get.
 * Which of these the page shows comes from the API (GET /credits/methods, the
 * switches on the admin console's Payment Methods page): a hidden wallet is
 * left out, a disabled one greyed out as "Unavailable".
 */
const methods = [
    // Scanned with any bank or e-wallet app on the receipt page, which shows the code.
    { value: 'qrph', label: 'QR Ph', logo: qrph, height: 'h-[35px]' },
    { value: 'gcash', label: 'GCash', logo: gcash, height: 'h-[35px]' },
    { value: 'maya', label: 'Maya', logo: maya, height: 'h-[40px]' },
    { value: 'shopeepay', label: 'ShopeePay', logo: shopee, height: 'h-[35px]' },
    { value: 'grabpay', label: 'GrabPay', logo: grab, height: 'h-[35px]' },
];

const monthly = 40;
const yearly = 360;

export default function Credits() {

    useEffect(() => {
        if (!window.localStorage.token) {
            window.location.replace('/signin');
            return;
        }
        setPageLoading(false);
    }, []);

    const { balance, error: balanceError } = useCredits();

    const [pageLoading, setPageLoading] = useState(true);
    const [amount, setAmount] = useState(() => {
        const requested = parseInt(new URLSearchParams(window.location.search).get('amount') ?? '');
        return requested >= TOPUP_MINIMUM ? String(Math.min(requested, TOPUP_MAXIMUM)) : String(TOPUP_MINIMUM);
    });
    const [payment, setPayment] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // Each wallet the switches leave on the page, enabled or disabled; undefined while loading.
    const [offered, setOffered] = useState<{ id: string, state: string }[]>();

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('/credits/methods');
                const json = await res.json();
                if (res.ok && Array.isArray(json.methods)) {
                    setOffered(json.methods);
                    return;
                }
            } catch (err) { }
            // As the API does when it can't read the switches: every wallet on. The top-up itself is still checked.
            setOffered(methods.map((item) => ({ id: item.value, state: 'enabled' })));
        })();
    }, []);

    if (pageLoading) {
        return (<div className='w-screen h-screen text-center flex flex-col bg-white justify-center'><Loader /></div>)
    }

    const credits = Number(amount);
    const valid = Number.isInteger(credits) && credits >= TOPUP_MINIMUM && credits <= TOPUP_MAXIMUM;
    const method = methods.find((item) => item.value === payment);
    const shown = methods
        .filter((item) => offered?.some((o) => o.id === item.value))
        .map((item) => ({ ...item, off: offered?.find((o) => o.id === item.value)?.state !== 'enabled' }));
    const noWallet = offered !== undefined && !shown.some((item) => !item.off);

    async function topup() {

        if (!valid) {
            setError(`Please enter a whole amount from ${peso(TOPUP_MINIMUM)} to ${peso(TOPUP_MAXIMUM)}`);
            return;
        }

        if (!payment) {
            setError('Please select a payment method');
            return;
        }

        setError('');
        setLoading(true);

        try {
            const res = await api.post('/credits/topup', { amount: credits, payment });
            const json = await res.json();
            if (res.ok) {
                window.location.replace(`/transaction/${json.id}?u=1`);
                return;
            }
            setError(errorMessage(json));
        } catch (err) {
            setError(errorMessage(err));
        }

        setLoading(false);
    }

    return (
        <Layout nav='credits' title='Credits'>
            <section className="mx-auto w-full max-w-5xl px-5 py-8 sm:px-8">

                <ListHeader
                    eyebrow="Billing"
                    title="Credits"
                    description="Top up with an e-wallet, then use your credits to buy or renew remote accounts." />

                <PaymentNoticeBanner className='mt-6' />

                <div className='mt-6 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_340px]'>

                    <div className='space-y-5'>

                        <section className='rounded-2xl border border-slate-200 bg-white p-5 sm:p-6'>
                            <h2 className='text-sm font-bold uppercase tracking-wider text-slate-400'>1. Top-up Amount</h2>

                            <div className='mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4'>
                                {presets.map((preset) => (
                                    <button
                                        key={preset}
                                        type='button'
                                        disabled={loading}
                                        onClick={() => { setAmount(String(preset)); setError(''); }}
                                        className={`min-h-[52px] rounded-xl text-base font-bold ring-1 ring-inset transition ${credits === preset
                                            ? 'bg-emerald-50 text-emerald-700 ring-2 ring-emerald-500'
                                            : 'bg-white text-slate-700 ring-slate-200 hover:ring-slate-300'}`}>
                                        {peso(preset)}
                                    </button>
                                ))}
                            </div>

                            <label className='mt-5 block text-xs font-semibold text-slate-500' htmlFor='amount'>Or enter an amount</label>
                            <div className='mt-1.5 flex flex-row items-center gap-2'>
                                <span className='text-sm font-semibold text-slate-500'>₱</span>
                                <input
                                    id='amount'
                                    type='number'
                                    inputMode='numeric'
                                    min={TOPUP_MINIMUM}
                                    max={TOPUP_MAXIMUM}
                                    step={1}
                                    value={amount}
                                    disabled={loading}
                                    onChange={(e) => { setAmount(e.target.value); setError(''); }}
                                    className='min-h-[40px] w-40 rounded-xl border-0 bg-white px-3 text-sm text-slate-900 ring-1 ring-inset ring-slate-200 outline-none transition focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50'
                                />
                            </div>
                            <div className={`mt-2 text-xs ${amount && !valid ? 'text-red-500' : 'text-slate-500'}`}>
                                Minimum {peso(TOPUP_MINIMUM)}, maximum {peso(TOPUP_MAXIMUM)}. 1 credit = ₱1.
                            </div>
                        </section>

                        <section className='rounded-2xl border border-slate-200 bg-white p-5 sm:p-6'>
                            <h2 className='text-sm font-bold uppercase tracking-wider text-slate-400'>2. Payment Method</h2>
                            {method && <div className='mt-1 text-xs'>You have selected <span className='font-bold'>{method.label}</span> as your payment method</div>}

                            {offered === undefined && <div className='mt-4 text-xs text-slate-400'>Loading payment methods…</div>}

                            {noWallet && (
                                <div className='mt-4 rounded-xl bg-amber-50 p-3 text-xs leading-relaxed text-amber-800 ring-1 ring-amber-200'>
                                    Top-ups are unavailable right now. Please check back later. Your credits can still be used as usual.
                                </div>
                            )}

                            <div className='mt-4 flex flex-row flex-wrap gap-3'>
                                {shown.map((item) => (
                                    <button
                                        key={item.value}
                                        type='button'
                                        disabled={loading || item.off}
                                        title={item.off ? `${item.label} is unavailable right now` : undefined}
                                        className='disabled:cursor-not-allowed'
                                        onClick={() => { setPayment(payment === item.value ? '' : item.value); setError(''); }}>
                                        <div className={`group relative flex flex-col items-center ${item.off ? 'opacity-40' : ''}`}>
                                            <img src={item.logo} alt={item.label} className={`${item.height} rounded-lg p-0.5 ring-2 transition ${payment === item.value ? 'ring-blue-500' : 'ring-transparent'}`} />
                                            {item.off && <span className='mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-500'>Unavailable</span>}
                                            {!item.off && <span className="absolute z-10 top-10 scale-0 transition-all rounded bg-gray-800 p-2 text-xs text-white group-hover:scale-100 whitespace-nowrap">{item.label}</span>}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </section>

                    </div>

                    <div className='space-y-5 lg:sticky lg:top-6 lg:self-start'>

                        <div className='rounded-2xl bg-gradient-to-br from-emerald-600 to-emerald-700 p-5 text-white shadow-lg shadow-emerald-700/20 sm:p-6'>
                            <div className='text-xs font-bold uppercase tracking-wider text-emerald-100'>Available credits</div>
                            <div className='mt-2 text-3xl font-bold tracking-tight'>{balance === undefined ? '—' : peso(balance)}</div>
                            <div className='mt-3 text-xs text-emerald-100'>1 month = {monthly} credits · 1 year = {yearly} credits</div>
                            {balanceError && <div className='mt-2 text-xs text-red-100' dangerouslySetInnerHTML={{ __html: balanceError }} />}
                        </div>

                        <div className='rounded-2xl border border-slate-200 bg-white p-5 sm:p-6'>
                            <h2 className='mb-4 text-sm font-bold uppercase tracking-wider text-slate-400'>Top-up Summary</h2>

                            <div className='flex items-start justify-between gap-4 py-1.5 text-sm'>
                                <span className='text-slate-500'>Credits</span><span className='text-right font-semibold text-slate-800'>{valid ? peso(credits) : '—'}</span>
                            </div>

                            <div className='mt-2 flex items-center justify-between border-t border-slate-100 pt-3'>
                                <span className='text-sm font-bold text-slate-700'>Total</span><span className='text-xl font-bold text-emerald-600'>{valid ? peso(credits) : '—'}</span>
                            </div>

                            {error && <div className='mt-3 text-xs font-medium text-red-500' dangerouslySetInnerHTML={{ __html: error }} />}

                            <Button tone='green' className='mt-4 w-full' loading={loading} disabled={loading || noWallet} onClick={topup}>
                                Top up{valid ? ` ${peso(credits)}` : ''}
                            </Button>

                            <div className='mt-3 text-xs text-slate-500'>A receipt will be sent to <span className='font-bold'>{window.localStorage['email']}</span>. Credits are added as soon as the payment goes through.</div>
                        </div>

                    </div>

                </div>

            </section>
        </Layout>
    );
}
